import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { 
  Trophy, 
  Users, 
  Calendar,
  MapPin,
  Coins,
  Crown,
  Medal,
  Plus,
  ChevronRight,
  Shield,
  Target,
  Award,
  Clock,
  AlertCircle
} from "lucide-react";

const TournamentsPage = ({ user, onLogout, refreshUser }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("open");
  
  // Katılma dialog
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [joinData, setJoinData] = useState({
    teamName: "",
    clubId: null,
    isTemporary: false,
    players: []
  });
  const [playerInput, setPlayerInput] = useState({ name: "", position: "" });
  
  // Kullanıcının kulübü
  const [myClub, setMyClub] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tournamentsRes, clubRes] = await Promise.all([
        axios.get(`${API}/tournaments`),
        axios.get(`${API}/clubs/my`).catch(() => ({ data: null }))
      ]);
      
      setTournaments(tournamentsRes.data || []);
      setMyClub(clubRes.data);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClick = (tournament) => {
    setSelectedTournament(tournament);
    setJoinData({
      teamName: myClub?.name || "",
      clubId: myClub?.id || null,
      isTemporary: !myClub,
      players: []
    });
    setShowJoinDialog(true);
  };

  const handleAddPlayer = () => {
    if (playerInput.name.trim()) {
      setJoinData({
        ...joinData,
        players: [...joinData.players, { ...playerInput, id: Date.now().toString() }]
      });
      setPlayerInput({ name: "", position: "" });
    }
  };

  const handleRemovePlayer = (playerId) => {
    setJoinData({
      ...joinData,
      players: joinData.players.filter(p => p.id !== playerId)
    });
  };

  const handleJoinTournament = async () => {
    if (!joinData.teamName.trim()) {
      toast.error("Takım adı gerekli");
      return;
    }
    
    if (joinData.players.length < 5) {
      toast.error("En az 5 oyuncu eklemelisiniz");
      return;
    }

    try {
      const response = await axios.post(`${API}/tournaments/${selectedTournament.id}/join`, joinData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowJoinDialog(false);
        fetchData();
        if (refreshUser) refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Katılım başarısız");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "registration":
        return <Badge className="bg-green-500">Kayıt Açık</Badge>;
      case "ongoing":
        return <Badge className="bg-blue-500">Devam Ediyor</Badge>;
      case "completed":
        return <Badge className="bg-gray-500">Tamamlandı</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "cup":
        return <Badge variant="outline" className="border-amber-500 text-amber-600">🏆 Kupa</Badge>;
      case "league":
        return <Badge variant="outline" className="border-blue-500 text-blue-600">📊 Lig</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const openTournaments = tournaments.filter(t => t.status === "registration");
  const ongoingTournaments = tournaments.filter(t => t.status === "ongoing");
  const completedTournaments = tournaments.filter(t => t.status === "completed");

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1f2224]' : ''}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#1f2224]' : 'bg-gray-50'}`}>
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Trophy className="h-8 w-8" />
                Lig & Turnuvalar
              </h1>
              <p className="text-amber-100 mt-1">
                Yarışmalara katıl, şampiyon ol!
              </p>
            </div>
            <div className="hidden md:flex gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <p className="text-2xl font-bold">{openTournaments.length}</p>
                <p className="text-xs text-amber-100">Açık Turnuva</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <p className="text-2xl font-bold">{ongoingTournaments.length}</p>
                <p className="text-xs text-amber-100">Devam Eden</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`mb-6 flex flex-wrap gap-1 ${isDark ? 'bg-[#2a2d2f]' : ''}`}>
            <TabsTrigger value="open" className={`flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 ${isDark ? 'data-[state=active]:bg-[#3a3d40] data-[state=active]:text-white text-gray-300' : ''}`}>
              <Target className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Kayıt Açık</span>
              <span className="sm:hidden">Açık</span>
              <span className="ml-1">({openTournaments.length})</span>
            </TabsTrigger>
            <TabsTrigger value="ongoing" className={`flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 ${isDark ? 'data-[state=active]:bg-[#3a3d40] data-[state=active]:text-white text-gray-300' : ''}`}>
              <Trophy className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Devam Eden</span>
              <span className="sm:hidden">Devam</span>
              <span className="ml-1">({ongoingTournaments.length})</span>
            </TabsTrigger>
            <TabsTrigger value="completed" className={`flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 ${isDark ? 'data-[state=active]:bg-[#3a3d40] data-[state=active]:text-white text-gray-300' : ''}`}>
              <Award className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Tamamlanan</span>
              <span className="sm:hidden">Biten</span>
              <span className="ml-1">({completedTournaments.length})</span>
            </TabsTrigger>
          </TabsList>

          {/* Kayıt Açık Turnuvalar */}
          <TabsContent value="open">
            {openTournaments.length === 0 ? (
              <Card className={`text-center py-12 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
                <CardContent>
                  <Trophy className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-300'}`} />
                  <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Şu an açık turnuva bulunmuyor</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {openTournaments.map((tournament) => (
                  <Card 
                    key={tournament.id} 
                    className={`hover:shadow-lg transition-shadow cursor-pointer ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}
                    onClick={() => navigate(`/tournaments/${tournament.id}`)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className={`text-lg ${isDark ? 'text-white' : ''}`}>{tournament.name}</CardTitle>
                          <CardDescription className={isDark ? 'text-gray-400' : ''}>{tournament.description}</CardDescription>
                        </div>
                        {getTypeBadge(tournament.type)}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className={`flex flex-wrap items-center gap-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          <span className="font-medium">{tournament.teams?.length || 0}</span>/{tournament.maxTeams} takım
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(tournament.startDate).toLocaleDateString('tr-TR')}
                        </div>
                        {tournament.registrationDeadline && (
                          <div className={`flex items-center gap-1 ${
                            new Date(tournament.registrationDeadline) < new Date() ? 'text-red-500' : 'text-orange-500'
                          }`}>
                            <Clock className="h-4 w-4" />
                            Son: {new Date(tournament.registrationDeadline).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                      </div>
                      
                      {/* Son başvuru tarihi geçmiş uyarısı */}
                      {tournament.registrationDeadline && new Date(tournament.registrationDeadline) < new Date() && (
                        <div className={`flex items-center gap-2 p-2 rounded-lg text-xs ${isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'}`}>
                          <AlertCircle className="h-4 w-4" />
                          Son başvuru tarihi geçmiş
                        </div>
                      )}
                      
                      {/* Katılan Takımlar Önizlemesi */}
                      {tournament.teams?.length > 0 && (
                        <div className={`rounded-lg p-3 ${isDark ? 'bg-[#3a3d40]' : 'bg-gray-50'}`}>
                          <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Katılan Takımlar:</p>
                          <div className="flex flex-wrap gap-2">
                            {tournament.teams?.slice(0, 4).map((team, idx) => (
                              <div key={idx} className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs ${isDark ? 'bg-[#2a2d2f] border-[#4a4d50] text-gray-200' : 'bg-white'}`}>
                                <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[10px] font-bold">
                                  {team.name?.charAt(0)}
                                </div>
                                <span className="truncate max-w-[80px]">{team.name}</span>
                              </div>
                            ))}
                            {tournament.teams?.length > 4 && (
                              <div className={`px-2 py-1 rounded-full border text-xs ${isDark ? 'bg-[#2a2d2f] border-[#4a4d50] text-gray-400' : 'bg-white text-gray-500'}`}>
                                +{tournament.teams.length - 4} daha
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {tournament.city && (
                        <div className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <MapPin className="h-4 w-4" />
                          {tournament.city} {tournament.district && `/ ${tournament.district}`}
                        </div>
                      )}
                      
                      {/* Ödüller */}
                      <div className={`rounded-lg p-3 ${isDark ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                        <p className={`text-xs font-medium mb-2 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>Ödüller:</p>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="flex items-center gap-1">
                            <Crown className="h-3 w-3 text-yellow-500" />
                            +{tournament.rewards?.first?.popularity || 50} puan
                          </span>
                          <span className="flex items-center gap-1">
                            <Medal className="h-3 w-3 text-gray-400" />
                            +{tournament.rewards?.second?.popularity || 30} puan
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-1 text-emerald-600 font-medium">
                          <Coins className="h-4 w-4" />
                          {tournament.entryFee} Kredi
                        </div>
                        <Button 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleJoinClick(tournament);
                          }}
                          disabled={(user?.credits || 0) < tournament.entryFee}
                        >
                          Katıl
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Devam Eden Turnuvalar */}
          <TabsContent value="ongoing">
            {ongoingTournaments.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Trophy className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Devam eden turnuva yok</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {ongoingTournaments.map((tournament) => (
                  <Card key={tournament.id} className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/tournaments/${tournament.id}`)}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{tournament.name}</CardTitle>
                          <CardDescription>{tournament.teams?.length || 0} takım</CardDescription>
                        </div>
                        {getTypeBadge(tournament.type)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        {getStatusBadge(tournament.status)}
                        <Button variant="outline" size="sm">
                          Detaylar
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tamamlanan Turnuvalar */}
          <TabsContent value="completed">
            {completedTournaments.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Trophy className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Tamamlanan turnuva yok</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {completedTournaments.map((tournament) => (
                  <Card key={tournament.id} className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/tournaments/${tournament.id}`)}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{tournament.name}</CardTitle>
                          {tournament.winnerName && (
                            <CardDescription className="flex items-center gap-1 text-amber-600">
                              <Crown className="h-4 w-4" />
                              Şampiyon: {tournament.winnerName}
                            </CardDescription>
                          )}
                        </div>
                        {getTypeBadge(tournament.type)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button variant="outline" size="sm" className="w-full">
                        Sonuçları Gör
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Katılma Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Turnuvaya Katıl</DialogTitle>
            <DialogDescription>
              {selectedTournament?.name} - Katılım ücreti: {selectedTournament?.entryFee} kredi
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Takım Adı */}
            <div className="space-y-2">
              <Label>Takım Adı</Label>
              <Input
                value={joinData.teamName}
                onChange={(e) => setJoinData({ ...joinData, teamName: e.target.value })}
                placeholder="Takım adınızı girin"
              />
            </div>
            
            {/* Kulüp Seçimi */}
            {myClub && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                <Shield className="h-5 w-5 text-emerald-600" />
                <div className="flex-1">
                  <p className="font-medium text-emerald-800">{myClub.name}</p>
                  <p className="text-xs text-emerald-600">Kulübünüz ile katılıyorsunuz</p>
                </div>
              </div>
            )}
            
            {/* Oyuncu Listesi */}
            <div className="space-y-2">
              <Label>Oyuncu Kadrosu (En az 5 oyuncu)</Label>
              
              {/* Oyuncu ekleme */}
              <div className="flex gap-2">
                <Input
                  value={playerInput.name}
                  onChange={(e) => setPlayerInput({ ...playerInput, name: e.target.value })}
                  placeholder="Oyuncu adı"
                  className="flex-1"
                />
                <Input
                  value={playerInput.position}
                  onChange={(e) => setPlayerInput({ ...playerInput, position: e.target.value })}
                  placeholder="Pozisyon"
                  className="w-24"
                />
                <Button type="button" size="icon" onClick={handleAddPlayer}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Oyuncu listesi */}
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {joinData.players.map((player, idx) => (
                  <div key={player.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">
                      {idx + 1}. {player.name} {player.position && `(${player.position})`}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 h-6 w-6 p-0"
                      onClick={() => handleRemovePlayer(player.id)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-gray-500">
                {joinData.players.length}/5 oyuncu eklendi
              </p>
            </div>
            
            <Button 
              className="w-full" 
              onClick={handleJoinTournament}
              disabled={joinData.players.length < 5 || !joinData.teamName.trim()}
            >
              <Coins className="h-4 w-4 mr-2" />
              Katıl ({selectedTournament?.entryFee} Kredi)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TournamentsPage;
